import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { IClassifier, IClassifierEditModel } from '../models/Classifier';
import { environment as ENV } from '../../environments/environment';
import { tap } from 'rxjs/operators';
import { AppService } from './app.service';

const storage: {
    requestedTypes: string[],
    items: IClassifier[]
} = {
    requestedTypes: [],
    items: []
};

/**
 * Classifier service
 */
@Injectable({ providedIn: "root" })
export class ClassifierService {
    constructor(private http: HttpClient, app: AppService) {
        this.isEn = app.currentLanguage == 'en';
    }

    private readonly apiUrl: string = `${ENV.apiUrl}/classifiers`;
    private readonly isEn: boolean;

    /**
     * Get classifiers.
     * @param type Comma-separated classifier types or array of types.
     * @param cache Use browser cache
     */
    get(type: string | string[], cache?: boolean): Observable<IClassifier[]> {
        let types: string[] = [];

        if (type) {
            if (typeof type === 'string') {
                types = type.split(',');
            } else {
                types = type;
            }
        }

        const items: IClassifier[] = [];
        const loadTypes: string[] = [];

        if (cache) {
            types.forEach(t => {
                if (storage.requestedTypes.includes(t)) {
                    items.push(...storage.items.filter(n => n.Type == t));
                } else {
                    loadTypes.push(t);
                }
            });
        } else {
            loadTypes.push(...types);
        }

        if (loadTypes.length) {
            const params: any = { type: loadTypes.join(',') };

            let req = this.http.get<IClassifier[]>(this.apiUrl, { params }).pipe(this.translate());

            if (cache) {
                req = req.pipe(tap(data => {
                    storage.items.push(...data);
                    storage.requestedTypes.push(...loadTypes);
                }));
            }

            return req;
        } else {
            return of(items);
        }
    }

    getTypes(): Observable<string[]> {
        const url = `${this.apiUrl}/types`;
        return this.http.get<string[]>(url);
    }

    getById(id: string): Observable<IClassifier> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<IClassifier>(url).pipe(this.translate());
    }

    getByCode(code: string, cache?: boolean): Observable<IClassifier> {
        let item: IClassifier;

        if (cache) {
            item = storage.items.find(i => i.Code === code);
        }

        if (item) {
            return of(item);
        } else {
            const url = `${this.apiUrl}/getByCode(${code})`;

            let req = this.http.get<IClassifier>(url).pipe(this.translate());

            if (cache) {
                req = req.pipe(tap(data => {
                    storage.items.push(data);
                }));
            }

            return req;
        }
    }

    /**
     * Get UDR related classifiers.
     * @returns
     */
    getUdr(): Observable<IClassifier[]> {
        const url = `${this.apiUrl}/udr`;
        return this.http.get<IClassifier[]>(url).pipe(this.translate());
    }

    create(item: IClassifierEditModel): Observable<IClassifier> {
        return this.http.post<IClassifier>(this.apiUrl, item).pipe(tap(() => {
            this.clearCache();
        }));
    }

    update(id: string, item: IClassifierEditModel): Observable<IClassifier> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.put<IClassifier>(url, item).pipe(tap(() => {
            this.clearCache();
        }));
    }

    delete(id: string): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.delete(url).pipe(tap(() => {
            this.clearCache();
        }));
    }

    private clearCache() {
        storage.items = [];
        storage.requestedTypes = [];
    }

    private translate<T extends IClassifier | IClassifier[]>() {
        return tap((data: T) => {
            ([].concat(data)).forEach(t => {
                t.Value = this.isEn ? t.ValueEN : t.ValueLV;
            });
        });
    }
}
